import { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { Provider } from "react-redux";

// Redux
import { store } from "./store";
import { getAuth } from "./slices/auth";
import setAuthToken from "./utils/auth";
import NotFound from "./components/routing/NotFound";
import AdsProvider from "./components/contexts/AdProvider";
import PrivacyPolicy from "./components/documentation/PrivacyPolicy";
import TermsAndConditions from "./components/documentation/TermsAndConditions";
import Disclaimer from "./components/documentation/Disclaimer";
import AcceptableUsePolicy from "./components/documentation/AcceptableUsePolicy";
import Home from "./components/home/Home";
import Search from "./components/search/Search";
import Directory from "./components/directory/Directory";
import TopicsAndCollections from "./components/interstitial/TopicsAndCollections";
import About from "./components/about/About";
import Speaking from "./components/speaking/Speaking";
import Contact from "./components/contact/Contact";
import Blog from "./components/blog/Blog";
import TopicBySlug from "./components/topic/TopicBySlug";
import CollectionBySlug from "./components/collection/CollectionBySlug";
import VerificationConfirm from "./components/placeholders/VerificationConfirm";
import ForgotMyPassword from "./components/auth/ForgotMyPassword";
import ResetPassword from "./components/auth/ResetPassword";
import Account from "./components/account/Account";
import PrivateRoute from "./components/routing/PrivateRoute";
import PublicRoute from "./components/routing/PublicRoute";
import Events from "./components/events/Events";
import PipelineDashboard from "./components/pipeline/PipelineDashboard";
import IngestionForm from "./components/pipeline/IngestionForm";
import ReviewInterface from "./components/pipeline/ReviewInterface";
import CookiePolicy from "./components/documentation/CookiePolicy";

if (localStorage.token) {
  setAuthToken(localStorage.token);
}

function App() {
  useEffect(() => {
    store.dispatch(getAuth());
  }, []);


  return (
    <Provider store={store}>
      <AdsProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            {/* Home - Public */}
            <Route element={<PublicRoute title="Home" />}>
              <Route path="/home" element={<Home />} />
            </Route>

            {/* About - Public */}
            <Route element={<PublicRoute title="About" />}>
              <Route path="/about" element={<About />} />
            </Route>

            {/* Speaking - Public */}
            <Route element={<PublicRoute title="Speaking" />}>
              <Route path="/speaking" element={<Speaking />} />
            </Route>

            {/* Contact - Public */}
            <Route element={<PublicRoute title="Contact" />}>
              <Route path="/contact" element={<Contact />} />
            </Route>

            {/* Blog - Public */}
            {/* <Route element={<PublicRoute title="Blog" />}>
              <Route path="/blog" element={<Blog />} />
            </Route> */}

            {/* Search - Public */}
            <Route element={<PublicRoute title="Search" />}>
              <Route path="/search" element={<Search />} />
            </Route>

            {/* Events - Public */}
            <Route element={<PublicRoute title="Events" />}>
              <Route path="/events" element={<Events />} />
            </Route>

            {/* Directory - Public */}
            <Route element={<PublicRoute title="Directory" />}>
              <Route path="/directory" element={<Directory />} />
            </Route>

            {/* Account - Private */}
            <Route element={<PrivateRoute allowedGroups={["Consumers", "Admin"]} title="Account" />}>
              <Route path="/account" element={<Account />} />
            </Route>

            {/* Interstitial - Topics & Collections - Public */}
            <Route element={<PublicRoute title="Topics & Collections" />}>
              <Route path="/topics-and-collections" element={<TopicsAndCollections />} />
            </Route>

            {/* Topic - Public */}
            <Route element={<PublicRoute title="Topic" />}>
              <Route path="/topic/:topic_slug" element={<TopicBySlug />} />
            </Route>

            {/* Collection - Public */}
            <Route element={<PublicRoute title="Collection" />}>
              <Route path="/collection/:collection_slug" element={<CollectionBySlug />} />
            </Route>

            {/* Verification confirmation - Public */}
            <Route
              path="/verification/confirm/:profileId"
              element={<VerificationConfirm />}
            />

            {/* Forgot my Password - Public */}
            <Route element={<PublicRoute title="Forgot my Password" />}>
              <Route path="/forgot-my-password" element={<ForgotMyPassword />} />
            </Route>

            {/* Forgot my Password (Recovery) - Public */}
            <Route element={<PublicRoute title="Password Recovery" />}>
              <Route path="/forgot-my-password/recovery" element={<ResetPassword />} />
            </Route>

            {/* Documentation - Public */}
            <Route element={<PublicRoute title="Terms of Service" />}>
              <Route path="/terms-of-service" element={<TermsAndConditions />} />
            </Route>
            <Route element={<PublicRoute title="Privacy Policy" />}>
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            </Route>
            <Route element={<PublicRoute title="Cookie Policy" />}>
              <Route path="/cookie-policy" element={<CookiePolicy />} />
            </Route>

            {/* Pipeline - Private (Role-Based Access) */}
            
            {/* Dashboard: View all content items and pipeline status */}
            <Route element={<PrivateRoute allowedGroups={["Admin", "Content_Manager", "Content_Reviewer", "Analyst"]} title="Pipeline Dashboard" />}>
              <Route path="/pipeline/dashboard" element={<PipelineDashboard />} />
            </Route>
            
            {/* Ingest: Create new content items */}
            <Route element={<PrivateRoute allowedGroups={["Admin", "Content_Manager", "Content_Creator"]} title="Ingest Content" />}>
              <Route path="/pipeline/ingest" element={<IngestionForm />} />
            </Route>
            
            {/* Review: pending decision + publish after approve */}
            <Route element={<PrivateRoute allowedGroups={["Admin", "Content_Manager", "Content_Reviewer"]} title="Review Content" />}>
              <Route path="/pipeline/review/:contentId" element={<ReviewInterface mode="review" />} />
            </Route>
            
            {/* Details: read-only snapshot for any status */}
            <Route element={<PrivateRoute allowedGroups={["Admin", "Content_Manager", "Content_Reviewer", "Analyst"]} title="Content Details" />}>
              <Route path="/pipeline/details/:contentId" element={<ReviewInterface mode="details" />} />
            </Route>

            {/* Not Found */}
            <Route path="/404" element={<NotFound />} />
            <Route path="/" element={<Navigate replace to="/home" />} />
            <Route path="*" element={<Navigate replace to="/404" />} />
          </Routes>
        </Router>
      </AdsProvider>
    </Provider>
  );
}

export default App;
